import { fetchAuthSession } from "aws-amplify/auth";
import { useCallback, useRef, useState } from "react";

import type { Source, StreamPhase } from "./utils";

type UseAISearchReturn = {
  phase: StreamPhase;
  statusStep: string | null;
  sources: Source[];
  content: string;
  error: string | null;
  visibleSourceCount: number;
  thinkingInsight: string;
  abortControllerRef: React.MutableRefObject<AbortController | null>;
  streamSearch: (query: string) => Promise<void>;
};

export const useAISearch = (aiSearchApiUrl: string): UseAISearchReturn => {
  const [phase, setPhase] = useState<StreamPhase>("idle");
  const [statusStep, setStatusStep] = useState<string | null>(null);
  const [sources, setSources] = useState<Source[]>([]);
  const [content, setContent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [visibleSourceCount, setVisibleSourceCount] = useState(0);
  const [thinkingInsight, setThinkingInsight] = useState("");
  const thinkingBufferRef = useRef("");
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleStructuredStream = useCallback(
    async (reader: ReadableStreamDefaultReader<Uint8Array>) => {
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);
            switch (event.type) {
              case "status":
                setPhase("searching");
                setStatusStep(event.step);
                break;
              case "source":
                setPhase("sources");
                setSources((prev) => {
                  const next = [...prev, event as Source];
                  // Stagger source pill animations
                  setTimeout(
                    () => setVisibleSourceCount((c) => c + 1),
                    prev.length * 400,
                  );
                  return next;
                });
                break;
              case "thinking":
                setPhase("thinking");
                if (event.step) setStatusStep(event.step);
                break;
              case "thinking_content": {
                thinkingBufferRef.current += event.content;
                const insightLines = thinkingBufferRef.current
                  .split("\n")
                  .filter((l: string) => l.trim());
                if (insightLines.length > 0) {
                  let line = insightLines[insightLines.length - 1].trim();
                  line = line.replace(/\*\*/g, "").replace(/^-\s*/, "");
                  if (line.length > 100) line = line.slice(0, 97) + "...";
                  if (line) setThinkingInsight(line);
                }
                break;
              }
              case "token":
                setPhase("streaming");
                setContent((prev) => prev + event.content);
                break;
              case "done":
                setPhase("complete");
                break;
              case "source_content":
                setSources((prev) =>
                  prev.map((s, i) => ({
                    ...s,
                    content: event.contents[i] ?? s.content,
                  })),
                );
                break;
              case "error":
                setError(event.message);
                setPhase("complete");
                break;
            }
          } catch {
            console.error("Failed to parse NDJSON event:", line);
          }
        }
      }

      // Handle any remaining buffer
      if (buffer.trim()) {
        try {
          const event = JSON.parse(buffer);
          if (event.type === "done") setPhase("complete");
          else if (event.type === "error") {
            setError(event.message);
            setPhase("complete");
          }
        } catch {
          /* ignore trailing partial */
        }
      }
    },
    [],
  );

  const streamSearch = useCallback(
    async (query: string) => {
      if (!aiSearchApiUrl) return;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Reset state
      setContent("");
      setSources([]);
      setError(null);
      setStatusStep(null);
      setVisibleSourceCount(0);
      setThinkingInsight("");
      thinkingBufferRef.current = "";
      setPhase("searching");

      try {
        // Get Cognito ID token for auth
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        const response = await fetch(`${aiSearchApiUrl}/api/search/stream/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(idToken && { Authorization: `Bearer ${idToken}` }),
          },
          body: JSON.stringify({
            query,
            provider: "vector",
            structured: true,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("Response body is not readable");
        }

        await handleStructuredStream(reader);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        console.error("Streaming error:", err);
        setError(err instanceof Error ? err.message : "An error occurred");
        setPhase("complete");
      }
    },
    [handleStructuredStream, aiSearchApiUrl],
  );

  return {
    phase,
    statusStep,
    sources,
    content,
    error,
    visibleSourceCount,
    thinkingInsight,
    abortControllerRef,
    streamSearch,
  };
};
